/**
 * NOS-160: visible AI-setup status in the builder. Polls
 * org_ai_profiles.generation_status while a job is pending/running, reloads
 * the canvas when it succeeds, and offers Retry on failure — replaces the
 * old silent `.catch(console.warn)` behavior.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { supabase } from "@/lib/supabase"

const POLL_MS = 5000
/** pending/running older than this is treated as stuck → show retry. */
const STALE_MS = 3 * 60 * 1000

type GenStatus = "pending" | "running" | "succeeded" | "failed"

interface StatusRow {
  generation_status: GenStatus | null
  generation_error: string | null
  generation_updated_at: string | null
}

export interface GenerationStatusBarProps {
  orgId: string
  /** Called when a run finishes successfully so the canvas can reload content. */
  onGenerated: () => void
}

export function GenerationStatusBar({ orgId, onGenerated }: GenerationStatusBarProps) {
  const [row, setRow] = useState<StatusRow | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const prevStatus = useRef<GenStatus | null>(null)

  const fetchStatus = useCallback(async () => {
    const { data } = await supabase
      .from("org_ai_profiles")
      .select("generation_status, generation_error, generation_updated_at")
      .eq("org_id", orgId)
      .maybeSingle()
    setRow((data as StatusRow | null) ?? null)
  }, [orgId])

  useEffect(() => {
    void fetchStatus()
  }, [fetchStatus])

  const status = row?.generation_status ?? null
  const active = status === "pending" || status === "running"

  useEffect(() => {
    if (!active) return
    const t = setInterval(() => void fetchStatus(), POLL_MS)
    return () => clearInterval(t)
  }, [active, fetchStatus])

  useEffect(() => {
    if (prevStatus.current && prevStatus.current !== "succeeded" && status === "succeeded") {
      onGenerated()
    }
    prevStatus.current = status
  }, [status, onGenerated])

  if (!status || dismissed || status === "succeeded") return null

  const updatedAt = row?.generation_updated_at ? Date.parse(row.generation_updated_at) : 0
  const stale = active && updatedAt > 0 && Date.now() - updatedAt > STALE_MS
  const failed = status === "failed" || stale

  const handleRetry = async () => {
    if (retrying) return
    setRetrying(true)
    try {
      await supabase
        .from("org_ai_profiles")
        .update({
          generation_status: "pending",
          generation_error: null,
          generation_updated_at: new Date().toISOString(),
        })
        .eq("org_id", orgId)
      await fetchStatus()
      await supabase.functions.invoke("generate-site-content", { body: { org_id: orgId } })
      await fetchStatus()
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div
      data-testid="generation-status-bar"
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 14px",
        background: failed ? "rgba(239,68,68,0.12)" : "rgba(255,255,255,0.05)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        fontSize: 12,
        color: failed ? "#fca5a5" : "rgba(255,255,255,0.7)",
      }}
    >
      {!failed && (
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#22c55e",
            animation: "pulse 1.4s ease-in-out infinite",
          }}
        />
      )}
      <span style={{ flex: 1 }}>
        {failed
          ? row?.generation_error ?? "AI content generation didn't finish."
          : "AI is writing your site content — it appears here as it finishes."}
      </span>
      {failed && (
        <button
          type="button"
          data-testid="generation-retry-btn"
          onClick={() => void handleRetry()}
          disabled={retrying}
          style={{
            padding: "4px 12px",
            borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.15)",
            background: "rgba(255,255,255,0.06)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 600,
            cursor: retrying ? "default" : "pointer",
          }}
        >
          {retrying ? "Retrying…" : "Retry"}
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        style={{
          border: "none",
          background: "transparent",
          color: "rgba(255,255,255,0.4)",
          cursor: "pointer",
          fontSize: 13,
          padding: 2,
        }}
      >
        ✕
      </button>
    </div>
  )
}
